import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ScheduleIcon from '@common/components/icons/ScheduleIcon';
import ChevronIcon from '@common/components/icons/ChevronIcon';
import CalendarIcon from '@common/components/icons/CalendarIcon';
import TimeIcon from '@common/components/icons/TimeIcon';
import { DAYS_OF_WEEK, FREQUENCY_OPTIONS } from '../../../utils/scheduleUtils';
import * as styles from '../ScheduleReportPage.module.css';

/**
 * ExecutionScheduleSection Component
 * 
 * Section for configuring the schedule (frequency, time, days)
 */
function ExecutionScheduleSection({
  expanded,
  onToggle,
  frequency,
  onFrequencyChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  scheduleTime,
  onScheduleTimeChange,
  selectedDays,
  onDayToggle,
}) {
  const { t } = useTranslation();
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className={styles.section}>
      <button
        type="button"
        className={styles.sectionHeader}
        onClick={onToggle}
      >
        <div className={styles.sectionTitleWrapper}>
          <ScheduleIcon className={styles.sectionIcon} />
          <span className={styles.sectionTitle}>
            {t('scheduledReports.sections.executionSchedule.title')}
          </span>
        </div>
        <ChevronIcon className={expanded ? styles.chevronExpanded : styles.chevron} />
      </button>

      {expanded && (
        <div className={styles.sectionContent}>
          {/* Frequency */}
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="frequency">
              {t('scheduledReports.sections.executionSchedule.frequency')}
            </label>
            <select
              id="frequency"
              className={styles.select}
              value={frequency}
              onChange={(e) => onFrequencyChange(e.target.value)}
            >
              {FREQUENCY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(`scheduledReports.schedule.${opt.value}`)}
                </option>
              ))}
            </select>
          </div>

          {/* Start Date / End Date */}
          <div className={styles.fieldRow}>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="startDate">
                {t('scheduledReports.sections.executionSchedule.startDate')}
              </label>
              <div className={styles.dateInputWrapper}>
                <CalendarIcon className={styles.inputIcon} />
                <input
                  id="startDate"
                  type="date"
                  className={styles.dateInput}
                  value={startDate}
                  min={today}
                  onChange={(e) => onStartDateChange(e.target.value)}
                />
              </div>
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="endDate">
                {t('scheduledReports.sections.executionSchedule.endDate')}{' '}
                <span className={styles.optional}>
                  ({t('common.optional')})
                </span>
              </label>
              <div className={styles.dateInputWrapper}>
                <CalendarIcon className={styles.inputIcon} />
                <input
                  id="endDate"
                  type="date"
                  className={styles.dateInput}
                  value={endDate}
                  min={startDate || today}
                  onChange={(e) => onEndDateChange(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Time */}
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="scheduleTime">
              {t('scheduledReports.sections.executionSchedule.time')}
            </label>
            <div className={styles.timeInputWrapper}>
              <TimeIcon className={styles.inputIcon} />
              <input
                id="scheduleTime"
                type="time"
                className={styles.timeInput}
                value={scheduleTime}
                onChange={(e) => onScheduleTimeChange(e.target.value)}
              />
            </div>
          </div>

          {/* Repeats On (for weekly) */}
          {frequency === 'weekly' && (
            <div className={styles.fieldGroup}>
              <span className={styles.fieldLabel}>
                {t('scheduledReports.sections.executionSchedule.repeatsOn')}
              </span>
              <div className={styles.daysSelector}>
                {DAYS_OF_WEEK.map((day) => (
                  <button
                    key={day.value}
                    type="button"
                    className={`${styles.dayButton} ${
                      selectedDays.includes(day.value) ? styles.selected : ''
                    }`}
                    onClick={() => onDayToggle(day.value)}
                    title={day.fullLabel}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

ExecutionScheduleSection.propTypes = {
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  frequency: PropTypes.oneOf(['daily', 'weekly', 'monthly']).isRequired,
  onFrequencyChange: PropTypes.func.isRequired,
  startDate: PropTypes.string.isRequired,
  onStartDateChange: PropTypes.func.isRequired,
  endDate: PropTypes.string.isRequired,
  onEndDateChange: PropTypes.func.isRequired,
  scheduleTime: PropTypes.string.isRequired,
  onScheduleTimeChange: PropTypes.func.isRequired,
  selectedDays: PropTypes.arrayOf(PropTypes.string).isRequired,
  onDayToggle: PropTypes.func.isRequired,
};

export default ExecutionScheduleSection;

